import CheckIcon from '@mui/icons-material/Check'
import PersonIcon from '@mui/icons-material/Person'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Typography } from '@mui/material'
import { getGridStringOperators } from '@mui/x-data-grid-pro'
import RenderContactNameCell from 'components/Contacts/grid/cells/RenderContactNameCell'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { formatPhoneNumber } from '../../Contacts/formatPhoneNumber'
import RenderCompanyChip from '../../RenderChipComponents/RenderCompanyChip'

export const formatDayjsDate = dateStr => {
  if (dateStr) {
    return dayjs(dateStr).format('MM/DD/YYYY hh:mm A')
  } else {
    return null
  }
}

export const renderContactsColumns = (
  organizations = [],
  contacts = [],
  users = [],
  density = 'standard',
  handleRowClick?: any,
  toggleExpanded?: any
) => {
  const containsOperator = getGridStringOperators().find(op => op.value === 'contains')

  // Custom operator that wraps the 'contains' logic to invert it
  const customNotContainsOperator = {
    ...containsOperator,
    label: 'does not contain',
    value: 'notContains',
    getApplyFilterFn: (filterItem: any, column: any) => {
      const containsFn = containsOperator?.getApplyFilterFn?.(filterItem, column)
      if (!containsFn) {
        return null
      }
      return (cellValue: any) => !containsFn(cellValue, null as any, null as any, null as any)
    },
  }
  const densityStyles = getDensityStyles(density)
  const stringAvatar = name => {
    if (!name || typeof name !== 'string') {
      // Handle cases where name is undefined, null, or not a string
      return {
        children: (
          <Typography
            sx={{
              fontSize: densityStyles.fontSize,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            N/A
          </Typography>
        ),
      }
    }

    const nameParts = name.split(' ')

    if (nameParts.length < 2) {
      return {
        children: (
          <Typography
            sx={{
              fontSize: densityStyles.fontSize,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            {nameParts[0][0]}
          </Typography>
        ),
      }
    }

    return {
      children: (
        <Typography
          sx={{
            fontSize: densityStyles.fontSize,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {`${nameParts[0][0]}${nameParts[1][0]}`}
        </Typography>
      ),
    }
  }

  return [
    {
      field: 'name',
      aggregable: false,
      headerName: 'Name',
      minWidth: 120,
      flex: 1,
      valueFormatter: (_, row) => {
        const contact = contacts?.find(c => c?.contacts_uuid === row?.contacts_uuid)
        const value = row?.isParent ? row?.name : `${contact?.name}-${row?.name}`
        return value
      },
      valueGetter: (_, row) => row?.name,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        return (
          <RenderContactNameCell
            params={params}
            densityStyles={densityStyles}
            handleRowClick={handleRowClick}
            toggleExpanded={toggleExpanded}
            contacts={contacts}
          />
        )
      },
    },
    {
      field: 'organizations',
      headerName: 'Organization',
      minWidth: 100,
      flex: 0.7,
      valueGetter: params => {
        const orgId = params
          ? Array.isArray(params)
            ? params[0]
            : typeof params === 'object' && params.id
            ? params.id
            : null
          : null
        const arrayOrg = organizations.find(org => org.id === orgId)
        // return { orgId: orgId, arrayOrg: arrayOrg }
        return arrayOrg ? arrayOrg.name : ''
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const orgId =
          params &&
          params.row &&
          params.row.organizations &&
          Array.isArray(params.row.organizations) &&
          params.row.organizations[0]
            ? params.row.organizations[0]
            : null
        const currentOrg = organizations.find(org => org.org_uuid === orgId)
        const { PK } = currentOrg ?? {}

        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
              {currentOrg && orgId ? (
                <Link to={`/organizations/${PK}/details`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {/* <Box>{orgName}</Box> */}
                  <RenderCompanyChip option={currentOrg} />
                </Link>
              ) : null}
              {/* {orgId ? (
              <Link to={`/organizations/${orgId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box>{orgName}</Box>
              </Link>
            ) : (
              <Box>No Organization Assigned</Box>
            )} */}
            </Box>
          </Box>
        )
      },
    },
    {
      field: 'billingContact',
      headerName: 'Billing Contact',
      width: 150,
      valueGetter: params => {
        return params ?? params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const isBillingContact =
          params && params.value ? params.value : params?.row?.isBillingContact ? params?.row?.isBillingContact : false

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            {isBillingContact ? <CheckIcon sx={{ color: 'text.complete' }} /> : <CloseIcon sx={{ color: 'crimson' }} />}
          </Box>
        )
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 100,
      flex: 0.7,
    },
    // {
    //   field: 'firstName',
    //   headerName: 'First Name',
    //   width: 100,
    // },
    // {
    //   field: 'lastName',
    //   headerName: 'Last Name',
    //   width: 100,
    // },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <>
            {params && params.value && params.value !== 'No Phone' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box>{formatPhoneNumber(params.value)}</Box>
              </Box>
            ) : (
              <Box sx={{ color: 'text.secondary' }}>No Phone Number</Box>
            )}
          </>
        )
      },
    },
    {
      field: 'timeCreated',
      headerName: 'Created Time',
      width: 150,
      hidden: true,
      renderCell: params => {
        return <Box>{formatDayjsDate(params.value)}</Box>
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 100,
      headerAlign: 'center',
      valueGetter: (_, row) => {
        return row?.owner ? row?.owner?.name : 'No User Assigned'
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const { owner } = params?.row ?? {}
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {owner ? (
              <EntityAvatar
                name={owner?.name}
                photoUrl={owner?.image_512}
                userId={owner?.user_uuid}
                size={densityStyles.size}
                fallbackIcon={
                  <PersonIcon
                    sx={{
                      height: densityStyles?.iconSize,
                      width: densityStyles?.iconSize,
                    }}
                  />
                }
                tooltip
                enableNav
              />
            ) : (
              <Box>No User Assigned</Box>
            )}
          </Box>
        )
      },
    },
  ]
}

export default renderContactsColumns
